import { Sheet, SheetContent } from '@/components/ui/sheet';
import { OrganizationSwitcher, SignedIn, UserButton, useAuth } from '@clerk/clerk-react';
import { Outlet, createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import {
  BookOpen,
  ClipboardList,
  CloudUpload,
  FlaskConical,
  Home,
  Menu,
  MessageSquare,
  PhoneForwarded,
  Puzzle,
  Wrench,
} from 'lucide-react';
import React from 'react';
// We'll need to define and import routerContext type eventually
// import type { RouterContext } from '@/router'; // Example path

export const Route = createFileRoute('/_protected')({
  // beforeLoad can be used if we need to fetch data based on auth state *before* rendering.
  // However, for simple auth checks and redirects, Clerk's components are often cleaner.
  // We will handle auth state rendering within the component itself.
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { isSignedIn, isLoaded } = useAuth();
  const router = useRouter();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    // This effect will run when the component mounts and when isSignedIn or isLoaded changes.
    // If Clerk has loaded and the user is not signed in, redirect to a sign-in page.
    // We can use a dedicated sign-in route or Clerk's hosted pages.
    // For now, let's assume you might want to redirect to a route handled by Clerk components or a custom /sign-in page.
    if (isLoaded && !isSignedIn) {
      // Redirect to a general sign-in route or the root if you have Clerk's <SignedOut> there.
      // Or, you could redirect to a specific sign-in page you create e.g., '/sign-in'
      // router.navigate({ to: '/' }); // Or '/sign-in'
      // For this example, we'll redirect to login, assuming it will be replaced by Clerk SignIn component
      router.navigate({
        to: '/login/$',
        search: {
          redirect: router.state.location.href,
        },
      });
    }
  }, [isSignedIn, isLoaded, router]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // If signed in, render the protected content (Outlet) and UserButton.
  // If not signed in (and isLoaded is true), this part will typically not be reached due to the redirect.
  // However, as a fallback or if you don't redirect, SignedIn/SignedOut can control display.
  const sidebarContent = (
    <div className="flex h-full max-h-screen flex-col">
      {/* Sidebar Header - Organization Name / Switcher */}
      <div className="flex h-auto flex-col items-start justify-center gap-1 border-b px-4 py-3 lg:px-6">
        {/* Trapeze Logo and Name */}
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground">Trapeze</span>
        </div>
        {/* Clerk Organization Switcher */}
        <OrganizationSwitcher
          hidePersonal={true}
          skipInvitationScreen={true}
          organizationProfileMode="modal"
          createOrganizationMode="modal"
          appearance={{
            elements: {
              rootBox: 'w-full mt-0.5',
              organizationSwitcherTrigger:
                'w-full !justify-start p-0 border-none bg-transparent shadow-none hover:bg-transparent focus:ring-0 text-muted-foreground hover:text-foreground data-[state=open]:bg-transparent data-[state=open]:text-foreground',
              organizationPreviewTextContainer: 'text-xs font-normal ml-1',
              organizationPreviewImage: '!h-4 !w-4 !rounded-sm',
              chevronDownIcon: 'w-3.5 h-3.5 ml-auto',
              organizationSwitcherPopoverCard: 'text-sm -ml-1 mt-1 w-[calc(100%+8px)] shadow-lg',
            },
          }}
        />
      </div>
      {/* Navigation Links */}
      <div className="flex-1 overflow-auto py-2">
        <nav className="grid items-start gap-px px-2 text-sm font-medium lg:px-4">
          <NavSection title="GENERAL">
            <NavLink
              to="/dashboard"
              label="Home"
              icon={Home}
              onClick={() => setIsSidebarOpen(false)}
            />
            <NavLink
              to="/calls"
              search={{ page: 1 }}
              label="Conversation history"
              icon={MessageSquare}
              onClick={() => setIsSidebarOpen(false)}
            />
            <NavLink to="/knowledge-base" label="Knowledge base" icon={BookOpen} isPlaceholder />
            <NavLink
              to="/call-transfers"
              label="Call Transfers"
              icon={PhoneForwarded}
              isPlaceholder
            />
            <NavLink to="/integrations" label="Integrations" icon={Puzzle} isPlaceholder />
          </NavSection>

          <NavSection title="TEST AND IMPROVE">
            <NavLink to="/playground" label="Playground" icon={FlaskConical} isPlaceholder />
            <NavLink to="/trainings" label="Trainings" icon={ClipboardList} isPlaceholder />
            <NavLink to="/request-training" label="Request training" icon={Wrench} isPlaceholder />
            <NavLink to="/deployments" label="Deployments" icon={CloudUpload} isPlaceholder />
          </NavSection>
        </nav>
      </div>
      {/* Sidebar Footer - User Account */}
      <div className="mt-auto p-4 border-t">
        <UserButton
          showName={true}
          appearance={{
            elements: {
              userButtonTrigger: 'w-full flex items-center justify-start gap-2',
              avatarBox: 'h-8 w-8',
              userButtonName: 'text-sm',
            },
          }}
          userProfileProps={{
            appearance: {
              elements: {},
            },
          }}
        />
      </div>
    </div>
  );

  return (
    <SignedIn>
      <div className="grid min-h-screen w-full md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]">
        {/* Desktop Sidebar */}
        <aside className="hidden border-r bg-muted/40 md:block">{sidebarContent}</aside>

        {/* Mobile Sidebar */}
        <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
          <SheetContent
            side="left"
            className="w-[280px] p-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {sidebarContent}
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <div className="flex flex-col">
          {/* Mobile Header */}
          <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 md:hidden">
            <button
              type="button"
              onClick={() => setIsSidebarOpen(true)}
              className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-semibold text-foreground">Trapeze</span>
            <div className="ml-auto">
              <UserButton afterSignOutUrl="/login" />
            </div>
          </header>
          <main className="flex-1 p-4 sm:px-6 sm:py-0">
            <Outlet />
          </main>
        </div>
      </div>
    </SignedIn>
    // It's generally better to handle the "not signed in" case with a redirect (as above)
    // or at a higher level (e.g. in __root.tsx or App.tsx) with a global <SignedOut> component
    // that shows sign-in options if you don't want to redirect from each protected route.
    // If not redirecting, you might have a <SignedOut> here to prompt login:
    // <SignedOut>
    //   <div className="flex flex-col items-center justify-center h-screen">
    //     <p className="mb-4">Please sign in to access this page.</p>
    //     <SignInButton mode="modal" />
    //   </div>
    // </SignedOut>
  );
}

interface NavLinkProps {
  to: string;
  search?: Record<string, string | number | boolean>;
  label: string;
  icon: React.ElementType;
  isPlaceholder?: boolean;
  onClick?: () => void;
}

function NavLink({ to, search, label, icon: Icon, isPlaceholder, onClick }: NavLinkProps) {
  const { location } = useRouterState();
  const currentPath = location.pathname;
  const effectivePath = to || '';
  const isActive = !isPlaceholder && currentPath === effectivePath;

  const baseClasses = 'flex items-center gap-3 rounded-lg px-3 py-2 transition-all text-sm';
  const activeClasses = isActive ? 'bg-muted font-semibold text-primary' : 'text-muted-foreground';
  const hoverClasses = isActive ? '' : 'hover:bg-accent hover:text-accent-foreground';
  const placeholderClasses = isPlaceholder ? 'opacity-50 cursor-not-allowed' : '';

  const linkClasses =
    `${baseClasses} ${activeClasses} ${hoverClasses} ${placeholderClasses}`.trim();

  if (isPlaceholder) {
    return (
      <span className={linkClasses}>
        <Icon className="h-4 w-4" />
        {label}
      </span>
    );
  }

  return (
    <Link to={to} search={search} className={linkClasses} onClick={onClick}>
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );
}

function NavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="py-2">
      <h2 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
        {title}
      </h2>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
